/**
 * Fetch wrapper with JWT auth + auto-refresh + uniform error mapping.
 *
 * Public surface:
 *   - `api.get/post/patch/del`
 *   - `setAuthTokens` / `clearAuthTokens`
 *   - `ApiError` thrown on non-2xx with `{ code, message, fields? }`
 */

import { env } from '@/utils/env';
import type { ApiErrorPayload } from '@/types/domain';

const STORAGE_ACCESS = 'ola.auth.access';
const STORAGE_REFRESH = 'ola.auth.refresh';

interface FetchOptions extends Omit<RequestInit, 'body' | 'method'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Skip auth header (e.g. for /auth/tg-mini-app/). */
  anonymous?: boolean;
  /** Don't retry on 401. */
  noRefresh?: boolean;
}

export class ApiError extends Error {
  code: string;
  status: number;
  fields?: Record<string, string[]>;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message || 'API error');
    this.status = status;
    this.code = payload.code;
    if (payload.fields) this.fields = payload.fields;
    this.name = 'ApiError';
  }
}

export const tokenStore = {
  getAccess(): string | null {
    try {
      return localStorage.getItem(STORAGE_ACCESS);
    } catch {
      return null;
    }
  },
  getRefresh(): string | null {
    try {
      return localStorage.getItem(STORAGE_REFRESH);
    } catch {
      return null;
    }
  },
  set(access: string, refresh: string): void {
    try {
      localStorage.setItem(STORAGE_ACCESS, access);
      localStorage.setItem(STORAGE_REFRESH, refresh);
    } catch {
      // ignore (private mode etc.)
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_ACCESS);
      localStorage.removeItem(STORAGE_REFRESH);
    } catch {
      // ignore
    }
  },
};

export function setAuthTokens(access: string, refresh: string): void {
  tokenStore.set(access, refresh);
}

export function clearAuthTokens(): void {
  tokenStore.clear();
}

function buildQuery(query?: FetchOptions['query']): string {
  if (!query) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  const refresh = tokenStore.getRefresh();
  if (!refresh) return null;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${env.apiUrl}/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      });
      if (!res.ok) {
        clearAuthTokens();
        return null;
      }
      const data = (await res.json()) as { access: string };
      const existing = tokenStore.getRefresh();
      if (existing) tokenStore.set(data.access, existing);
      return data.access;
    } catch {
      clearAuthTokens();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

/** Cookie-based refresh for the web SPA. Returns a sentinel non-null string
 *  (the new access token in JSON, also delivered via cookie) so the caller can
 *  treat it the same as `refreshAccessToken`. The caller does NOT use the
 *  returned token directly — the cookie is what authenticates the retry.
 */
async function refreshAccessCookie(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${env.apiUrl}/auth/refresh-cookie/`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = (await res.json().catch(() => null)) as { access?: string } | null;
      return data?.access ?? 'cookie';
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function request<T>(path: string, method: string, options: FetchOptions = {}): Promise<T> {
  const url = `${env.apiUrl}${path}${buildQuery(options.query)}`;
  const headers = new Headers(options.headers);
  const hasHeaderAuth = (() => {
    if (options.anonymous) return false;
    const access = tokenStore.getAccess();
    if (access) {
      headers.set('Authorization', `Bearer ${access}`);
      return true;
    }
    return false;
  })();

  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.body);
  }

  const init: RequestInit = { method, headers, body };
  if (options.signal) init.signal = options.signal;
  // When we don't have a Bearer token (web SPA cookie flow), opt into
  // credentialed fetch so the browser sends `ola_access` / `ola_refresh`.
  if (!hasHeaderAuth && !options.anonymous) {
    init.credentials = 'include';
  }
  const res = await fetch(url, init);

  if (res.status === 401 && !options.noRefresh && !options.anonymous) {
    const newToken = hasHeaderAuth
      ? await refreshAccessToken()
      : await refreshAccessCookie();
    if (newToken) {
      return request<T>(path, method, { ...options, noRefresh: true });
    }
    clearAuthTokens();
  }

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const payload: ApiErrorPayload =
      isJson && data && typeof data === 'object' && 'error' in data
        ? (data as { error: ApiErrorPayload }).error
        : { code: `http_${res.status}`, message: `Request failed: ${res.status}` };
    throw new ApiError(res.status, payload);
  }

  return data as T;
}

export const api = {
  get: <T,>(path: string, options?: FetchOptions): Promise<T> => request<T>(path, 'GET', options),
  post: <T,>(path: string, options?: FetchOptions): Promise<T> => request<T>(path, 'POST', options),
  patch: <T,>(path: string, options?: FetchOptions): Promise<T> =>
    request<T>(path, 'PATCH', options),
  put: <T,>(path: string, options?: FetchOptions): Promise<T> => request<T>(path, 'PUT', options),
  del: <T,>(path: string, options?: FetchOptions): Promise<T> =>
    request<T>(path, 'DELETE', options),
};
